class LeadIntakeBooking extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.lead-intake__form');
    this.summaryInput = this.querySelector('[data-summary-input]');
    this.firstNameTargets = this.querySelectorAll('[data-first-name]');
    this.advisorNameTargets = this.querySelectorAll('[data-advisor-display-name]');
    this.advisorSchedulingCopy = this.querySelector('[data-advisor-scheduling-copy]');
    this.monthLabel = this.querySelector('[data-month-label]');
    this.calendarGrid = this.querySelector('[data-calendar-grid]');
    this.timeOptionsTarget = this.querySelector('[data-time-options]');
    this.emailInput = this.querySelector('input[name="email"]');
    this.countryInput = this.querySelector('select[name="country"]');
    this.selectedDateLabel = this.querySelector('[data-selected-date-label]');
    this.bookingSummary = this.querySelector('[data-booking-summary]');
    this.bookingError = this.querySelector('[data-booking-error]');
    this.requestBookingButton = this.querySelector('[data-request-booking]');
    this.prevMonthButton = this.querySelector('[data-prev-month]');
    this.nextMonthButton = this.querySelector('[data-next-month]');
    this.confirmationSummary = this.querySelector('[data-confirmation-summary]');
    this.confirmationWhen = this.querySelector('[data-confirmation-when]');
    this.confirmationEmail = this.querySelector('[data-confirmation-email]');
    this.confirmationAdvisorName = this.querySelector('[data-confirmation-advisor-name]');
    this.timezoneSelect = this.querySelector('[data-booking-timezone-select]');
    this.timezoneLabel = this.querySelector('[data-booking-timezone-label]');

    this.apiBaseUrl = (this.dataset.apiBaseUrl || 'https://swede-booking-api.vercel.app').replace(/\/$/, '');
    this.bookingSource = this.dataset.bookingSource || 'swede_lead_intake_booking';
    this.bookingConfig = {
      durationMs: 1800000,
      timezone: 'America/Los_Angeles',
      locale: 'en-us',
      monthOffsets: [0, 1, 2],
    };
    this.currentMonthOffset = 0;
    this.availableMonthOffsets = [0, 1, 2];
    this.availabilityByDate = new Map();
    this.availabilityTimezone = this.bookingConfig.timezone;
    this.availabilityDurationMs = this.bookingConfig.durationMs;
    this.availabilityEmail = '';
    this.availabilityAdvisorDisplayName = '';
    this.availabilityAdvisorEmail = '';
    this.browserTimezone = this.resolveTimezone(this.getBrowserTimezone(), this.bookingConfig.timezone);
    this.selectedTimezone = this.browserTimezone;
    this.visibleMonth = this.getMonthForOffset(this.currentMonthOffset);
    this.selectedBooking = this.emptySelectedBooking();
    this.formData = null;
    this.isAvailabilityLoading = true;
    this.isBooking = false;
    this.availabilityError = '';
    this.availabilityRequestId = 0;

    if (!this.form || !this.calendarGrid || !this.timeOptionsTarget) return;

    this.initializeCountry();
    this.getAttributionContext();

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.querySelectorAll('[data-edit-details]').forEach((button) => {
      button.addEventListener('click', () => this.showStep(1));
    });
    this.prevMonthButton?.addEventListener('click', () => this.changeMonth(-1));
    this.nextMonthButton?.addEventListener('click', () => this.changeMonth(1));
    this.requestBookingButton?.addEventListener('click', () => this.handleBookingRequest());
    this.querySelectorAll('[data-summary-chip]').forEach((button) => {
      button.addEventListener('click', () => this.addSummaryPrompt(button.dataset.summaryChip || ''));
    });
    this.summaryInput?.addEventListener('input', () => {
      this.summaryInput.setCustomValidity('');
      this.setError('projectSummary', '');
    });
    this.emailInput?.addEventListener('input', () => this.handleEmailInputChange());
    this.countryInput?.addEventListener('change', () => this.validateCountry(this.countryInput.value));
    this.timezoneSelect?.addEventListener('change', () => this.handleTimezoneChange());

    this.updateTimezoneControls();
    this.renderCalendar();
    this.renderTimeOptions();
    this.updateAdvisorCopy();
    this.updateSelectedBooking();
    this.initializeBookingApi();
  }

  initializeCountry() {
    if (this.countryInitialized || !this.countryInput) return;
    this.countryInitialized = true;

    // Storefront localization is only a suggestion; keep the visitor's later choice.
    let initialCountry;
    try {
      initialCountry = JSON.parse(this.dataset.initialCountry || 'null');
    } catch (error) {
      initialCountry = null;
    }
    if (!this.countryInput.value && ['CA', 'US'].includes(initialCountry)) {
      this.countryInput.value = initialCountry;
    }
  }

  validateCountry(country) {
    const message = ['CA', 'US'].includes(country) ? '' : 'Select your country';
    this.countryInput?.setCustomValidity(message);
    this.countryInput?.setAttribute('aria-invalid', String(Boolean(message)));
    this.setError('country', message);
    return !message;
  }

  async initializeBookingApi() {
    try {
      await this.loadBookingConfig();
      this.isAvailabilityLoading = false;
      this.renderCalendar();
      this.renderTimeOptions();
      this.updateSelectedBooking();
    } catch (error) {
      this.isAvailabilityLoading = false;
      this.availabilityByDate = new Map();
      this.availabilityError = 'Booking times are temporarily unavailable. Please try again in a moment.';
      this.clearAdvisorDisplayName();
      this.clearSelectedBooking();
      this.renderCalendar();
      this.renderTimeOptions();
      this.updateSelectedBooking({ clearError: false });
      this.setBookingError(this.availabilityError);
    }
  }

  async loadBookingConfig() {
    const config = await this.requestJSON('/api/booking/config');
    const monthOffsets = Array.isArray(config.monthOffsets)
      ? config.monthOffsets.map((offset) => Number(offset)).filter(Number.isFinite)
      : this.bookingConfig.monthOffsets;

    this.bookingConfig = {
      ...this.bookingConfig,
      durationMs: Number(config.durationMs) || this.bookingConfig.durationMs,
      timezone: config.timezone || this.bookingConfig.timezone,
      locale: config.locale || this.bookingConfig.locale,
      monthOffsets: monthOffsets.length ? monthOffsets : this.bookingConfig.monthOffsets,
    };
    this.availableMonthOffsets = [...this.bookingConfig.monthOffsets].sort((a, b) => a - b);
    this.currentMonthOffset = this.availableMonthOffsets.includes(0) ? 0 : this.availableMonthOffsets[0] || 0;
    this.selectedTimezone = this.resolveTimezone(this.selectedTimezone, this.bookingConfig.timezone);
    this.updateTimezoneControls();
    this.visibleMonth = this.getMonthForOffset(this.currentMonthOffset);
    this.availabilityTimezone = this.getSelectedTimezone();
    this.availabilityDurationMs = this.bookingConfig.durationMs;
  }

  async refreshAvailability(monthOffset = this.currentMonthOffset, options = {}) {
    const { clearSelection = false, keepBookingError = false } = options;
    const requestId = this.nextAvailabilityRequestId();

    if (clearSelection) this.clearSelectedBooking();

    this.currentMonthOffset = monthOffset;
    this.visibleMonth = this.getMonthForOffset(monthOffset);
    this.isAvailabilityLoading = true;
    this.availabilityError = '';
    this.renderCalendar();
    this.renderTimeOptions();
    this.updateSelectedBooking({ clearError: !keepBookingError });

    try {
      const contactEmail = this.getAvailabilityEmail();
      const payload = this.buildAvailabilityPayload(monthOffset, contactEmail);
      const availability = await this.requestAvailability(payload);

      if (!this.isCurrentAvailabilityRequest(requestId)) return;

      this.availabilityTimezone = availability.timezone || this.getSelectedTimezone();
      this.availabilityDurationMs = Number(availability.durationMs) || this.bookingConfig.durationMs;
      this.availabilityEmail = contactEmail;
      this.setAvailabilityAdvisor(availability.advisor, contactEmail);
      this.availabilityByDate = this.sanitizeAvailabilityDates(availability.dates);
      this.reconcileSelectedBooking();
    } catch (error) {
      if (!this.isCurrentAvailabilityRequest(requestId)) return;

      this.availabilityByDate = new Map();
      this.availabilityError = 'Booking times are temporarily unavailable. Please try again in a moment.';
      this.clearAdvisorDisplayName();
      this.clearSelectedBooking();
    } finally {
      if (!this.isCurrentAvailabilityRequest(requestId)) return;

      this.isAvailabilityLoading = false;
      this.renderCalendar();
      this.renderTimeOptions();
      this.updateSelectedBooking({ clearError: !keepBookingError && !this.availabilityError });
      if (this.availabilityError) this.setBookingError(this.availabilityError);
    }
  }

  nextAvailabilityRequestId() {
    this.availabilityRequestId += 1;
    return this.availabilityRequestId;
  }

  isCurrentAvailabilityRequest(requestId) {
    return requestId === this.availabilityRequestId;
  }

  buildAvailabilityPayload(monthOffset, contactEmail = '') {
    return {
      email: contactEmail,
      timezone: this.getSelectedTimezone(),
      monthOffset,
    };
  }

  async requestAvailability(payload) {
    if (payload.email) {
      return this.requestJSON('/api/booking/availability', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    }

    const params = new URLSearchParams({
      timezone: payload.timezone,
      monthOffset: String(payload.monthOffset),
    });

    return this.requestJSON(`/api/booking/availability?${params.toString()}`);
  }

  sanitizeAvailabilityDates(dates = []) {
    return new Map((Array.isArray(dates) ? dates : []).map((date) => [date.date, {
      date: date.date,
      label: date.label,
      slots: (Array.isArray(date.slots) ? date.slots : []).map((slot) => ({
        startTime: slot.startTime,
        endTime: slot.endTime || '',
        timeLabel: slot.timeLabel || '',
      })),
    }]));
  }

  setAvailabilityAdvisor(advisor, email) {
    this.availabilityAdvisorDisplayName = this.getPublicAdvisorDisplayName(advisor);
    this.availabilityAdvisorEmail = this.availabilityAdvisorDisplayName ? this.normalizeEmail(email) : '';
    this.updateAdvisorCopy(this.availabilityAdvisorDisplayName);
  }

  getPublicAdvisorDisplayName(advisor) {
    const displayName = typeof advisor?.displayName === 'string' ? advisor.displayName.trim() : '';
    return displayName.length <= 80 ? displayName : '';
  }

  clearAdvisorDisplayName() {
    this.availabilityAdvisorDisplayName = '';
    this.availabilityAdvisorEmail = '';
    this.updateAdvisorCopy();
  }

  updateAdvisorCopy(displayName = this.availabilityAdvisorDisplayName) {
    const advisorName = displayName || 'Your Swede advisor';
    const schedulingName = displayName || 'your Swede advisor';

    this.advisorNameTargets?.forEach((target) => {
      target.textContent = advisorName;
    });

    if (this.advisorSchedulingCopy) {
      this.advisorSchedulingCopy.textContent = `Choose a time with ${schedulingName}.`;
    }

    if (this.confirmationAdvisorName) {
      this.confirmationAdvisorName.textContent = advisorName;
    }
  }

  async requestJSON(path, options = {}) {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    let body = null;

    try {
      body = await response.json();
    } catch (error) {
      body = null;
    }

    if (!response.ok) {
      const apiError = new Error(body?.error?.message || 'Request failed');
      apiError.status = response.status;
      apiError.code = body?.error?.code || '';
      apiError.body = body;
      throw apiError;
    }

    return body;
  }

  handleSubmit(event) {
    event.preventDefault();
    this.clearErrors();

    const data = this.getFormData();
    const allowedProjectTypes = ['renovation', 'new-build', 'other'];
    let hasErrors = !this.validateCountry(data.country);

    if (!data.firstName) {
      this.setError('firstName', 'First name is required');
      hasErrors = true;
    }

    if (!data.phone) {
      this.setError('phone', 'Phone number is required');
      hasErrors = true;
    } else if (data.phone.length < 7) {
      this.setError('phone', 'Enter a valid phone number');
      hasErrors = true;
    }

    if (!data.email) {
      this.setError('email', 'Email is required');
      hasErrors = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      this.setError('email', 'Enter a valid email');
      hasErrors = true;
    }

    if (!allowedProjectTypes.includes(data.projectType)) {
      this.setError('projectType', 'Pick a project type');
      hasErrors = true;
    }

    if (data.projectSummary.length < 10) {
      this.setError('projectSummary', 'Tell us a little more, at least a sentence');
      this.summaryInput?.setCustomValidity('Tell us a little more, at least a sentence');
      hasErrors = true;
    } else if (data.projectSummary.length > 4000) {
      this.setError('projectSummary', 'Please keep this under 4000 characters');
      this.summaryInput?.setCustomValidity('Please keep this under 4000 characters');
      hasErrors = true;
    }

    if (hasErrors || !this.form.checkValidity()) {
      this.form.classList.remove('is-shaking');
      window.requestAnimationFrame(() => this.form.classList.add('is-shaking'));
      this.form.reportValidity();
      return;
    }

    const previousEmail = this.formData?.email || '';
    const emailChanged = this.normalizeEmail(previousEmail) !== this.normalizeEmail(data.email);
    const needsContactAvailability = emailChanged || this.normalizeEmail(this.availabilityEmail) !== this.normalizeEmail(data.email);

    this.formData = data;
    this.firstNameTargets.forEach((target) => {
      target.textContent = data.firstName || 'there';
    });
    this.showStep(2);

    if (needsContactAvailability || !this.availabilityByDate.size) {
      this.currentMonthOffset = this.availableMonthOffsets.includes(0) ? 0 : this.availableMonthOffsets[0] || 0;
      this.refreshAvailability(this.currentMonthOffset, { clearSelection: true });
    }
  }

  getFormData() {
    const formData = new FormData(this.form);
    return {
      firstName: String(formData.get('firstName') || '').trim(),
      lastName: String(formData.get('lastName') || '').trim(),
      phone: String(formData.get('phone') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      country: String(formData.get('country') || ''),
      projectType: String(formData.get('projectType') || '').trim(),
      projectSummary: String(formData.get('projectSummary') || '').trim(),
    };
  }

  handleEmailInputChange() {
    if (!this.formData?.email) return;

    const currentEmail = this.normalizeEmail(this.emailInput?.value || '');
    if (currentEmail === this.normalizeEmail(this.formData.email)) return;

    this.availabilityEmail = '';
    this.clearAdvisorDisplayName();
    this.clearSelectedBooking();
    this.updateSelectedBooking();
  }

  getAvailabilityEmail() {
    return this.formData?.email ? String(this.formData.email).trim() : '';
  }

  normalizeEmail(email) {
    return String(email || '').trim().toLowerCase();
  }

  getBrowserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (error) {
      return '';
    }
  }

  getSelectedTimezone() {
    return this.resolveTimezone(this.selectedTimezone, this.bookingConfig.timezone);
  }

  resolveTimezone(timezone, fallback = 'America/Los_Angeles') {
    const candidate = String(timezone || '').trim();
    if (this.isValidTimezone(candidate)) return candidate;

    const fallbackTimezone = String(fallback || '').trim();
    if (this.isValidTimezone(fallbackTimezone)) return fallbackTimezone;

    return 'America/Los_Angeles';
  }

  isValidTimezone(timezone) {
    if (!timezone) return false;

    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
      return true;
    } catch (error) {
      return false;
    }
  }

  async handleTimezoneChange() {
    const nextTimezone = this.getTimezoneFromSelectValue(this.timezoneSelect?.value);
    if (nextTimezone === this.getSelectedTimezone()) {
      this.selectedTimezone = nextTimezone;
      this.updateTimezoneControls();
      return;
    }

    this.selectedTimezone = nextTimezone;
    this.availabilityTimezone = nextTimezone;
    this.clearSelectedBooking();
    this.updateTimezoneControls();
    this.updateSelectedBooking();

    if (this.formData?.email) {
      await this.refreshAvailability(this.currentMonthOffset, { clearSelection: true });
      return;
    }

    this.availabilityByDate = new Map();
    this.visibleMonth = this.getMonthForOffset(this.currentMonthOffset);
    this.renderCalendar();
    this.renderTimeOptions();
  }

  updateTimezoneControls() {
    const timezone = this.getSelectedTimezone();
    const selectValue = this.getTimezoneSelectValue(timezone);
    this.ensureTimezoneOption(selectValue);

    if (this.timezoneSelect) {
      this.timezoneSelect.value = selectValue;
    }

    if (this.timezoneLabel) {
      this.timezoneLabel.textContent = this.formatTimezoneLabel(timezone);
    }
  }

  getTimezoneSelectValue(timezone) {
    const displayZones = {
      'America/Vancouver': 'America/Los_Angeles',
      'America/Edmonton': 'America/Denver',
      'America/Winnipeg': 'America/Chicago',
      'America/Toronto': 'America/New_York',
    };

    return displayZones[timezone] || timezone;
  }

  getTimezoneFromSelectValue(value) {
    const selectedTimezone = this.resolveTimezone(value, this.bookingConfig.timezone);
    const browserTimezone = this.isValidTimezone(this.browserTimezone) ? this.browserTimezone : '';

    if (browserTimezone && this.getTimezoneSelectValue(browserTimezone) === selectedTimezone) {
      return browserTimezone;
    }

    return selectedTimezone;
  }

  ensureTimezoneOption(timezone) {
    if (!this.timezoneSelect || !timezone || typeof this.timezoneSelect.querySelectorAll !== 'function') return;

    const options = Array.from(this.timezoneSelect.querySelectorAll('option'));
    if (options.some((option) => option.value === timezone)) return;

    const documentRef = this.ownerDocument || this.timezoneSelect.ownerDocument;
    if (!documentRef) return;

    const option = documentRef.createElement('option');
    option.value = timezone;
    option.textContent = this.formatTimezoneLabel(timezone);
    this.timezoneSelect.append(option);
  }

  getFormattingLocale() {
    const locale = String(this.bookingConfig.locale || '').trim();
    return locale || undefined;
  }

  showStep(step) {
    this.blurActiveStepElement();
    this.dataset.currentStep = String(step);

    this.querySelectorAll('[data-step-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.stepPanel !== String(step);
    });

    this.querySelectorAll('[data-step-indicator]').forEach((indicator) => {
      const indicatorStep = Number(indicator.dataset.stepIndicator);
      indicator.classList.toggle('is-active', indicatorStep === step);
      indicator.classList.toggle('is-done', indicatorStep < step);
      const number = indicator.querySelector('.lead-intake__step-num');
      if (number) number.textContent = indicatorStep < step ? '✓' : String(indicatorStep);
    });

    this.queueStepScroll(step);
  }

  blurActiveStepElement() {
    const activeElement = document.activeElement;
    if (!activeElement || activeElement === document.body || !this.contains(activeElement)) return;

    if (typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }

  queueStepScroll(step) {
    this.stepScrollRequestId = (this.stepScrollRequestId || 0) + 1;
    const requestId = this.stepScrollRequestId;
    const requestAnimationFrame = window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : ((callback) => window.setTimeout(callback, 0));

    const scrollToStep = (behavior = 'smooth') => {
      if (requestId !== this.stepScrollRequestId) return;

      const panel = this.querySelector(`[data-step-panel="${step}"]`);
      if (!panel || panel.hidden) return;

      const target = step === 2
        ? panel.querySelector('.lead-intake__step-head') || panel.querySelector('.lead-intake__booking-cal') || panel
        : panel;
      const top = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - this.getStepScrollOffset());
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

      window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : behavior,
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(() => scrollToStep('smooth')));
    window.setTimeout(() => scrollToStep('auto'), 320);
  }

  getStepScrollOffset() {
    const header = document.querySelector('.shopify-section-header');
    const headerHeight = header?.getBoundingClientRect?.().height || 0;

    return Math.min(Math.max(headerHeight, 0) + 12, 180);
  }

  renderCalendar() {
    const year = this.visibleMonth.getFullYear();
    const month = this.visibleMonth.getMonth();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstDayOfWeek; i += 1) {
      cells.push('<span class="lead-intake__calendar-day is-empty" aria-hidden="true"></span>');
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day, 12, 0, 0, 0);
      const value = this.formatDateValue(date);
      const availability = this.availabilityByDate.get(value);
      const available = Boolean(availability?.slots?.length) && !this.isAvailabilityLoading;
      const selected = this.selectedBooking.date === value;
      const classes = [
        'lead-intake__calendar-day',
        available ? 'is-available' : 'is-disabled',
        selected ? 'is-selected' : '',
      ].filter(Boolean).join(' ');

      cells.push(`
        <button
          type="button"
          class="${classes}"
          data-booking-date="${value}"
          data-booking-date-label="${this.escapeHTML(availability?.label || this.formatDateLabel(date, { year: false }))}"
          ${available ? '' : 'disabled'}
        >
          ${day}
        </button>
      `);
    }

    if (this.monthLabel) {
      this.monthLabel.textContent = this.visibleMonth.toLocaleDateString(this.getFormattingLocale(), {
        month: 'long',
        year: 'numeric',
      });
    }

    this.calendarGrid.innerHTML = cells.join('');
    this.calendarGrid.querySelectorAll('[data-booking-date]').forEach((button) => {
      button.addEventListener('click', () => this.selectDate(button));
    });
    this.updateCalendarNav();
  }

  renderTimeOptions() {
    if (this.isAvailabilityLoading) {
      this.timeOptionsTarget.innerHTML = `
        <div class="lead-intake__time-empty">
          Loading available times...
        </div>
      `;
      if (this.selectedDateLabel) this.selectedDateLabel.textContent = 'Loading times';
      return;
    }

    if (this.availabilityError) {
      this.timeOptionsTarget.innerHTML = `
        <div class="lead-intake__time-empty">
          Booking times are temporarily unavailable.
        </div>
      `;
      if (this.selectedDateLabel) this.selectedDateLabel.textContent = 'Try again soon';
      return;
    }

    if (!this.availabilityByDate.size) {
      this.timeOptionsTarget.innerHTML = `
        <div class="lead-intake__time-empty">
          No dates are available this month. Try the next month.
        </div>
      `;
      if (this.selectedDateLabel) this.selectedDateLabel.textContent = 'No dates available';
      return;
    }

    if (!this.selectedBooking.date) {
      this.timeOptionsTarget.innerHTML = `
        <div class="lead-intake__time-empty">
          Pick a date to see available times.
        </div>
      `;
      if (this.selectedDateLabel) this.selectedDateLabel.textContent = 'Pick a date';
      return;
    }

    const dateAvailability = this.availabilityByDate.get(this.selectedBooking.date);
    const slots = dateAvailability?.slots || [];

    if (this.selectedDateLabel) this.selectedDateLabel.textContent = this.selectedBooking.dateLabel;

    if (!slots.length) {
      this.timeOptionsTarget.innerHTML = `
        <div class="lead-intake__time-empty">
          No times are available for this date.
        </div>
      `;
      return;
    }

    const groupedSlots = this.groupSlots(slots);
    this.timeOptionsTarget.innerHTML = groupedSlots.map(([label, groupSlots]) => `
      <div class="lead-intake__time-group">
        <span>${label}</span>
        ${groupSlots.map((slot) => `
          <button
            type="button"
            class="lead-intake__time-option ${this.selectedBooking.startTime === slot.startTime ? 'is-selected' : ''}"
            data-booking-start-time="${this.escapeHTML(slot.startTime)}"
          >
            ${this.escapeHTML(slot.timeLabel || this.formatSlotTime(slot.startTime))}
          </button>
        `).join('')}
      </div>
    `).join('');

    this.timeOptionsTarget.querySelectorAll('[data-booking-start-time]').forEach((button) => {
      button.addEventListener('click', () => this.selectTime(button));
    });
  }

  async changeMonth(direction) {
    const nextOffset = this.currentMonthOffset + direction;
    if (!this.availableMonthOffsets.includes(nextOffset) || this.isAvailabilityLoading) return;

    this.clearSelectedBooking();
    await this.refreshAvailability(nextOffset);
  }

  selectDate(button) {
    this.selectedBooking = {
      ...this.emptySelectedBooking(),
      date: button.dataset.bookingDate || '',
      dateLabel: button.dataset.bookingDateLabel || '',
    };

    this.renderCalendar();
    this.renderTimeOptions();
    this.updateSelectedBooking();
  }

  selectTime(button) {
    const startTime = button.dataset.bookingStartTime || '';
    const dateAvailability = this.availabilityByDate.get(this.selectedBooking.date);
    const slot = dateAvailability?.slots?.find((availableSlot) => availableSlot.startTime === startTime);

    if (!slot) {
      this.setBookingError('That time is no longer available. Please choose another time.');
      this.clearSelectedTime();
      this.renderCalendar();
      this.renderTimeOptions();
      this.updateSelectedBooking({ clearError: false });
      return;
    }

    this.selectedBooking = {
      ...this.selectedBooking,
      time: slot.timeLabel || this.formatSlotTime(slot.startTime),
      startTime: slot.startTime,
      endTime: slot.endTime || '',
      timezone: this.availabilityTimezone || this.getSelectedTimezone(),
      durationMs: this.availabilityDurationMs,
    };

    this.renderTimeOptions();
    this.updateSelectedBooking();
  }

  updateSelectedBooking(options = {}) {
    const { clearError = true } = options;
    const hasSlot = Boolean(this.selectedBooking.startTime);

    if (this.bookingSummary) {
      this.bookingSummary.innerHTML = hasSlot
        ? `<strong>${this.escapeHTML(this.selectedBooking.dateLabel)}</strong> at <strong>${this.escapeHTML(this.selectedBooking.time)}</strong>`
        : 'Choose a date and time to continue.';
    }

    if (this.requestBookingButton) {
      this.requestBookingButton.disabled = !hasSlot || this.isBooking;
    }

    if (clearError) this.setBookingError('');
  }

  async handleBookingRequest() {
    if (this.isBooking) return;

    if (!this.validateCountry(this.getFormData().country)) {
      this.showStep(1);
      this.countryInput?.focus();
      this.countryInput?.reportValidity();
      return;
    }

    if (!this.selectedBooking.startTime) {
      this.setBookingError('Choose a date and time');
      return;
    }

    const payload = this.buildBookingPayload();
    this.setBookingSubmitting(true);
    this.setBookingError('');

    try {
      const booking = await this.requestJSON('/api/booking/book', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      this.renderConfirmation(booking, payload);
      this.trackBookingConversion(booking, payload);
      this.dispatchEvent(new CustomEvent('lead-intake:booking-confirmed', {
        bubbles: true,
        detail: { booking, payload },
      }));
      this.showStep(3);
    } catch (error) {
      if (error.status === 409 || error.code === 'SLOT_UNAVAILABLE') {
        await this.refreshAvailability(this.currentMonthOffset, {
          clearSelection: true,
          keepBookingError: true,
        });
        this.setBookingError('That time is no longer available. Please choose another time.');
        return;
      }

      this.setBookingError('Booking is temporarily unavailable. Please try again in a moment or email hello@myswedehome.com.');
    } finally {
      this.setBookingSubmitting(false);
    }
  }

  buildBookingPayload() {
    const data = this.formData || this.getFormData();

    return {
      firstName: data.firstName,
      lastName: data.lastName || '',
      email: data.email,
      phone: data.phone,
      country: this.getFormData().country,
      projectType: data.projectType,
      projectSummary: data.projectSummary,
      selectedSlot: {
        startTime: this.selectedBooking.startTime,
        timezone: this.selectedBooking.timezone,
        durationMs: this.selectedBooking.durationMs,
      },
      pageUrl: window.location.href,
      source: this.bookingSource,
      companyWebsite: '',
    };
  }

  trackBookingConversion(booking, payload) {
    try {
      const event = this.buildBookingConversionEvent(booking, payload);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch (error) {
      // Analytics failures must not block the booked-state confirmation.
    }
  }

  buildBookingConversionEvent(booking, payload) {
    const attribution = this.getAttributionContext();
    const selectedSlot = payload?.selectedSlot || {};
    const eventId = this.createConversionEventId();
    const source = payload?.source || this.bookingSource;
    const eventTime = Math.floor(Date.now() / 1000);
    const durationMinutes = Math.round(Number(selectedSlot.durationMs || 0) / 60000) || undefined;
    const pageUrl = attribution.page_url || payload?.pageUrl || window.location.href;

    return {
      event: 'swede_lead_booking_confirmed',
      event_id: eventId,
      event_time: eventTime,
      action_source: 'web',
      event_source_url: pageUrl,
      google_event_name: 'generate_lead',
      meta_event_name: 'Lead',
      meta_event_id: eventId,
      pinterest_event_name: 'lead',
      pinterest_event_id: eventId,
      pinterest_lead_type: 'consultation_booking',
      conversion_type: 'lead_booking',
      lead_source: source,
      booking_source: source,
      method: 'lead_intake_booking',
      content_name: 'Lead intake booking',
      content_category: 'Lead',
      project_type: payload?.projectType || '',
      booking_start_time: selectedSlot.startTime || booking?.start || '',
      booking_timezone: selectedSlot.timezone || booking?.timezone || '',
      booking_duration_minutes: durationMinutes,
      page_url: pageUrl,
      page_path: attribution.page_path,
      page_title: attribution.page_title,
      page_referrer: attribution.page_referrer,
      ...attribution.params,
      ...attribution.cookies,
    };
  }

  getAttributionContext() {
    const stored = this.getStoredAttribution();
    const url = new URL(window.location.href);
    const params = this.getAttributionParams(url.searchParams);
    const cookies = this.getAttributionCookies();
    const context = {
      page_url: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_title: document.title || '',
      page_referrer: document.referrer || stored.page_referrer || '',
      params: {
        ...(stored.params || {}),
        ...params,
      },
      cookies,
    };

    this.setStoredAttribution(context);
    return context;
  }

  getAttributionParams(searchParams) {
    return [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'utm_id',
      'gclid',
      'gbraid',
      'wbraid',
      'fbclid',
      'epik',
      'msclkid',
      'ttclid',
      'li_fat_id',
    ].reduce((params, name) => {
      const value = searchParams.get(name);
      if (value) params[name] = value;
      return params;
    }, {});
  }

  getAttributionCookies() {
    return {
      ga_client_id: this.getCookieValue('_ga'),
      google_gcl_aw: this.getCookieValue('_gcl_aw'),
      google_gcl_dc: this.getCookieValue('_gcl_dc'),
      fbp: this.getCookieValue('_fbp'),
      fbc: this.getCookieValue('_fbc'),
      pinterest_epik: this.getCookieValue('_epik'),
      pinterest_unauth_id: this.getCookieValue('_pin_unauth'),
    };
  }

  getCookieValue(name) {
    try {
      const cookie = document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(`${name}=`));
      return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
    } catch (error) {
      return '';
    }
  }

  getStoredAttribution() {
    try {
      const raw = window.sessionStorage?.getItem('swedeLeadAttribution');
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  }

  setStoredAttribution(context) {
    try {
      window.sessionStorage?.setItem('swedeLeadAttribution', JSON.stringify({
        page_referrer: context.page_referrer,
        params: context.params,
      }));
    } catch (error) {
      // Storage can be unavailable in private browsing or blocked contexts.
    }
  }

  createConversionEventId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `swede_lead_booking_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  renderConfirmation(booking, payload) {
    const advisorName = this.getConfirmationAdvisorDisplayName(booking, payload);
    this.updateAdvisorCopy(advisorName);

    if (this.confirmationSummary) {
      const advisorFollowUp = advisorName
        ? `${this.escapeHTML(advisorName)} will be in touch beforehand if they have any questions.`
        : 'Your Swede advisor will be in touch beforehand if they have any questions.';
      this.confirmationSummary.innerHTML = `A calendar invite is on the way to <strong>${this.escapeHTML(payload.email || 'your email')}</strong>. ${advisorFollowUp}`;
    }

    if (this.confirmationWhen) {
      this.confirmationWhen.innerHTML = this.formatConfirmationWhen(booking, payload);
    }

    if (this.confirmationEmail) {
      this.confirmationEmail.textContent = payload.email || 'your email';
    }
  }

  getConfirmationAdvisorDisplayName(booking, payload) {
    const bookingAdvisorName = this.getPublicAdvisorDisplayName(booking?.advisor);
    if (bookingAdvisorName) return bookingAdvisorName;

    const payloadEmail = this.normalizeEmail(payload?.email || '');
    if (payloadEmail && payloadEmail === this.availabilityAdvisorEmail) {
      return this.availabilityAdvisorDisplayName;
    }

    return '';
  }

  reconcileSelectedBooking() {
    if (!this.selectedBooking.startTime) return;

    const dateAvailability = this.availabilityByDate.get(this.selectedBooking.date);
    const stillAvailable = dateAvailability?.slots?.some((slot) => slot.startTime === this.selectedBooking.startTime);
    if (!stillAvailable) this.clearSelectedBooking();
  }

  updateCalendarNav() {
    if (this.prevMonthButton) {
      this.prevMonthButton.disabled = !this.availableMonthOffsets.includes(this.currentMonthOffset - 1) || this.isAvailabilityLoading;
    }

    if (this.nextMonthButton) {
      this.nextMonthButton.disabled = !this.availableMonthOffsets.includes(this.currentMonthOffset + 1) || this.isAvailabilityLoading;
    }
  }

  setBookingSubmitting(isBooking) {
    this.isBooking = isBooking;

    if (this.requestBookingButton) {
      this.requestBookingButton.disabled = isBooking || !this.selectedBooking.startTime;
      this.requestBookingButton.innerHTML = isBooking
        ? 'Booking...'
        : 'Confirm Booking <span aria-hidden="true">&rarr;</span>';
    }
  }

  groupSlots(slots) {
    const groups = new Map();

    slots.forEach((slot) => {
      const label = this.getSlotGroup(slot);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(slot);
    });

    return [...groups.entries()];
  }

  getSlotGroup(slot) {
    const label = String(slot.timeLabel || '');
    if (/AM/i.test(label)) return 'Morning';
    if (/PM/i.test(label)) return 'Afternoon';

    const hourPart = new Intl.DateTimeFormat(this.getFormattingLocale(), {
      timeZone: this.availabilityTimezone || this.bookingConfig.timezone,
      hour: '2-digit',
      hour12: false,
      numberingSystem: 'latn',
    }).formatToParts(new Date(slot.startTime)).find((part) => part.type === 'hour');
    const hour = Number(hourPart?.value);

    return hour < 12 ? 'Morning' : 'Afternoon';
  }

  formatConfirmationWhen(booking, payload) {
    const startValue = booking?.start || payload.selectedSlot.startTime;
    const timezone = booking?.timezone || payload.selectedSlot.timezone || this.getSelectedTimezone();
    const start = new Date(startValue);
    const dateLabel = this.formatDateLabelInTimezone(start, timezone, { year: true });
    const timeLabel = this.formatTimeInTimezone(start, timezone);
    const durationLabel = this.formatDuration(payload.selectedSlot.durationMs);
    const timezoneLabel = this.formatTimezoneLabel(timezone);

    return `<strong>${this.escapeHTML(dateLabel)}</strong><br>${this.escapeHTML(timeLabel)} · ${this.escapeHTML(durationLabel)} · ${this.escapeHTML(timezoneLabel)}`;
  }

  formatSlotTime(startTime) {
    return this.formatTimeInTimezone(new Date(startTime), this.availabilityTimezone || this.getSelectedTimezone());
  }

  formatTimeInTimezone(date, timezone) {
    return new Intl.DateTimeFormat(this.getFormattingLocale(), {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }

  formatDateLabelInTimezone(date, timezone, options = {}) {
    return new Intl.DateTimeFormat(this.getFormattingLocale(), {
      timeZone: timezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      ...(options.year ? { year: 'numeric' } : {}),
    }).format(date);
  }

  formatDuration(durationMs) {
    const minutes = Math.round(Number(durationMs || 0) / 60000);
    return `${minutes || 30} minutes`;
  }

  formatTimezoneLabel(timezone) {
    const labels = {
      'America/Los_Angeles': 'Pacific Time',
      'America/Vancouver': 'Pacific Time',
      'America/Denver': 'Mountain Time',
      'America/Edmonton': 'Mountain Time',
      'America/Phoenix': 'Arizona Time',
      'America/Chicago': 'Central Time',
      'America/Winnipeg': 'Central Time',
      'America/New_York': 'Eastern Time',
      'America/Toronto': 'Eastern Time',
      'America/Anchorage': 'Alaska Time',
      'Pacific/Honolulu': 'Hawaii Time',
    };
    if (labels[timezone]) return labels[timezone];
    if (timezone === 'America/Los_Angeles') return 'Pacific (PT)';
    return String(timezone || '').replace(/_/g, ' ').replace(/\//g, ' / ');
  }

  emptySelectedBooking() {
    return {
      date: '',
      dateLabel: '',
      time: '',
      startTime: '',
      endTime: '',
      timezone: '',
      durationMs: 0,
    };
  }

  clearSelectedBooking() {
    this.selectedBooking = this.emptySelectedBooking();
  }

  clearSelectedTime() {
    this.selectedBooking = {
      ...this.selectedBooking,
      time: '',
      startTime: '',
      endTime: '',
      timezone: '',
      durationMs: 0,
    };
  }

  getMonthForOffset(offset) {
    const baseMonth = this.getCurrentMonthInTimezone();
    return new Date(baseMonth.year, baseMonth.month + offset, 1, 12, 0, 0, 0);
  }

  getCurrentMonthInTimezone() {
    const parts = new Intl.DateTimeFormat(this.getFormattingLocale(), {
      timeZone: this.getSelectedTimezone(),
      year: 'numeric',
      month: 'numeric',
    }).formatToParts(new Date());
    const year = Number(parts.find((part) => part.type === 'year')?.value) || new Date().getFullYear();
    const month = Number(parts.find((part) => part.type === 'month')?.value) - 1;

    return {
      year,
      month: Number.isFinite(month) ? month : new Date().getMonth(),
    };
  }

  formatDateValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  formatDateLabel(date, options = {}) {
    return date.toLocaleDateString(this.getFormattingLocale(), {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      ...(options.year ? { year: 'numeric' } : {}),
    });
  }

  addSummaryPrompt(label) {
    if (!this.summaryInput || !label) return;

    const prefix = this.summaryInput.value && !this.summaryInput.value.endsWith('\n') ? '\n' : '';
    this.summaryInput.value = `${this.summaryInput.value}${prefix}${label}: `;
    this.summaryInput.focus();
    this.summaryInput.selectionStart = this.summaryInput.value.length;
    this.summaryInput.selectionEnd = this.summaryInput.value.length;
    this.summaryInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  clearErrors() {
    this.querySelectorAll('[data-error-for]').forEach((error) => {
      error.textContent = '';
    });
    this.summaryInput?.setCustomValidity('');
  }

  setError(name, message) {
    const error = this.querySelector(`[data-error-for="${name}"]`);
    if (error) error.textContent = message;
  }

  setBookingError(message) {
    if (this.bookingError) this.bookingError.textContent = message;
  }

  escapeHTML(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }
}

if (!customElements.get('lead-intake-booking')) {
  customElements.define('lead-intake-booking', LeadIntakeBooking);
}
